"use client";
import { useEffect } from "react";
import { EnrichedProgram, ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { Instructor } from "@/common/instructor/types";
import { ProductViewTracker } from "@/components/ProductViewTracker";
import { useCourseCheckoutUrl } from "@/hooks/useCourseCheckoutUrl";
import { useCompareCtaUrl } from "@/components/ProductCompare/useCompareCtaUrl";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import {
  mainContent as mainContentClass,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import PartnershipDescription from "@/components/ProgramDetailLayout/templates/components/PartnershipDescription";
import { PartialPartner } from "@/common/partner/types";
import CollapsedContent from "./components/CollapsedContent";
import ISO6391 from "iso-639-1";
import { getAiTranslationLanguageLabels } from "@/lib/course/utils/getAiLanguages";
import CourseTaggedHeader from "@/components/CourseContent/templates/components/CourseTaggedHeader";
import AlumniTestimonials from "@/components/AlumniTestimonials";
import Highlights from "@/components/Highlights";
import CourseEnrollButton from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import OCPromoBanner from "@/components/CourseContent/ContentRefresh/OCPromoBanner";
import CourseAbout from "@/components/CourseContent/ContentOverview";
import MidPageCTA from "@/components/CourseContent/ContentRefresh/MidPageCTA";
import { useCourseDetailPricingData } from "@/lib/checkout-geo-pricing";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import RelatedProgram from "../common/utils/renderRelatedProgram";
import SocialShare from "@/components/SocialShare";
import EnterpriseCTA from "../../ContentRefresh/EnterpriseCTA";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import MoreAboutCourse from "@/components/CourseContent/ContentRefresh/MoreAboutCourse";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useUserEntitlements from "../../CourseContext/hooks/useUserEntitlements";
import useUserEnrollments from "../../CourseContext/hooks/useUserEnrollments";
import useCourseDetailsData from "../../CourseContext/hooks/useCourseDetailsData";
import isUserEnrolled from "@/lib/course/utils/isUserEnrolled";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";
import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import { getProgramSearchUrl } from "../common/utils/getProgramSearchUrl";
import CareerCards from "@/components/CareerCards";
import {
  CAREER_CARDS_VARIANTS,
  resolveCareerCardSkills,
  getIndustryKey,
} from "@/components/CareerCards/constants";
import { courseDetailPageEventConfig } from "../common/const/const";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import ProductCompare from "@/components/ProductCompare";
import useCompareFallbackUuids from "@/components/ProductCompare/useCompareFallbackUuids";
import StickyCTA from "@/components/CourseContent/ContentRefresh/StickyCTA";
import { CourseCurrencyBarSurface } from "@/components/CourseContent/templates/components/CourseCurrencySurfaces";
import Curriculum from "@/components/CourseContent/Curriculum";
import FAQs from "@/components/CourseContent/ContentRefresh/FAQs";
import useTranslations from "@/i18n/useTranslations";

type Props = {
  course: ProcessedCourse;
  program?: Program | null;
  programPacingType?: string;
  programDuration?: string;
  hoursPerWeekFormatted?: string;
  paymentOption: "free" | "upgradable" | "paid" | null;
  showCourseModes: boolean;
  category?: string;
  courseSlug?: string;
  enrichedPrograms?: EnrichedProgram[] | null;
  enrichedInstructors?: Instructor[];
};

export default function ValueOrientedUser({
  course,
  enrichedPrograms,
  enrichedInstructors,
  paymentOption,
  category,
  courseSlug,
}: Props) {
  const tCourse = useTranslations("Course");
  const tProgram = useTranslations("Program");
  const {
    mainContent,
    partnershipDescription,
    courseBenefitList,
    headerBackground,
    headerPricePosition,
    headerPriceLabel,
    simpleMobileHeader,
    alumniTestimonials,
    highlights,
    promoBanner,
    carouselData,
    relatedProgramsRedesign: {
      design2: expandableSection,
      designToDisplay,
      cardType,
    } = {},
    benefitsModule,
    testimonialsModule,
    bundleSavingsBanner,
    showDynamicBadges,
    careerCards,
    productCompare,
  } = useSingleCourseTrafficConfig({ course });

  const compareFallbackUuids = useCompareFallbackUuids({
    source: productCompare?.relatedProductsSource,
    relatedProductsFilters: productCompare?.relatedProductsFilters,
    enrichedPrograms,
    course,
  });

  //fetch enrollment data
  const { enrollments: userEnrollments } = useClientEnrollments();

  //fetch entitlement data
  const { entitlements: userEntitlements } = useClientEntitlements();

  const { setIsUserEnrolled, setUserEnrollments } = useUserEnrollments();
  const { setUserEntitled } = useUserEntitlements();

  const { setCourseDetailsData } = useCourseDetailsData();
  const titlePartner = getPartnersDisplayNames(course.partners);

  // Generate checkout URL with SKU and course run key
  const checkoutUrl = useCourseCheckoutUrl(course);
  const compareCtaUrl = useCompareCtaUrl(course);

  const leadWithPaidData = useLeadWithPaid(course);

  const { languageId, transcriptLanguages } = course.activeCourseRun || {};

  const isArchived = course.activeCourseRun
    ? isCourseArchived(course.activeCourseRun)
    : true;

  const { syllabusHtml, productFullDescription, video, productImageUrl } =
    course;
  const { weeksToComplete, minEffort, maxEffort } =
    course.activeCourseRun || {};

  const hasCurriculumSectionContent =
    (syllabusHtml?.length ?? 0) > 0 ||
    (course.prerequisitesHtml?.length ?? 0) > 0;
  const isCurriculumInMainContent = Boolean(
    mainContent?.some((c) => c.component === "curriculum") &&
      hasCurriculumSectionContent,
  );

  const courseHeaderVideo = {
    image: {
      src: productImageUrl,
    },
    src: video.src,
  };

  const formattedLanguage = languageId
    ? ISO6391.getNativeName(languageId.slice(0, 2))
    : undefined;
  const formattedTranscripts =
    transcriptLanguages?.map((transcript) =>
      ISO6391.getNativeName(transcript.slice(0, 2)),
    ) || [];

  const formattedAiLanguages = course.activeCourseRun
    ? getAiTranslationLanguageLabels(course.activeCourseRun)
    : [];

  const pricingData = useCourseDetailPricingData({ course });
  const coursePrice =
    pricingData?.discountedPrice ?? pricingData?.originalPrice ?? null;

  useEffect(() => {
    setCourseDetailsData({
      category: category,
      courseSlug: courseSlug,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, courseSlug]);

  useEffect(() => {
    if (userEntitlements && userEntitlements.length > 0) {
      const found =
        userEntitlements?.some(
          ({ courseUuid: userEntitledCourseUuid }) =>
            userEntitledCourseUuid === course.activeCourseRun?.uuid,
        ) || false;
      setUserEntitled(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEntitlements]);

  //set context enrollmment status
  useEffect(() => {
    if (userEnrollments && userEnrollments.length > 0) {
      setUserEnrollments(userEnrollments);
      const courseIds = course.activeCourseRuns?.map(({ key }) => key) || [];
      const isEnrolled = isUserEnrolled(userEnrollments, courseIds);
      setIsUserEnrolled(isEnrolled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEnrollments]);

  const componentList = [
    {
      key: "highlights",
      renderer: () => {
        return highlights ? (
          <Highlights
            className="bg-putty-300"
            title={highlights.title}
            items={highlights.items}
            partnerName={course.partners?.length ? course.partners[0].name : ""}
            isEnrollable={course.activeCourseRun?.isEnrollable || false}
            theme="light"
            {...(highlights.promo && {
              promo: {
                headline: highlights.promo.headline,
                subtext: highlights.promo.subtext,
                infoText: highlights.promo.infoText,
                headlineMobile: highlights.promo.headlineMobile,
                subtextMobile: highlights.promo.subtextMobile,
                ctaElement: coursePrice ? (
                  <CourseEnrollButton
                    buttonClass="flex-1"
                    courseRun={course.activeCourseRun!}
                    course={course}
                    location="highlights"
                    price={coursePrice}
                  />
                ) : (
                  <EnrollLink
                    buttonClass="flex-1"
                    courseRun={course.activeCourseRun!}
                    course={course}
                    location="highlights"
                  />
                ),
              },
            })}
          />
        ) : null;
      },
    },
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner && course.activeCourseRun?.isEnrollable ? (
          <OCPromoBanner
            course={course}
            courseRun={course.activeCourseRun}
            data={promoBanner}
          />
        ) : null;
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={titlePartner}
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "courseAbout",
      renderer: () => {
        return (
          <CourseAbout
            key="courseAbout"
            courseData={course}
            fullDescription={productFullDescription}
            video={courseHeaderVideo}
            title={tCourse("courseOverview")}
          />
        );
      },
    },
    {
      key: "collapsedContent",
      renderer: () => {
        return (
          <CollapsedContent
            key="collapsedContent"
            whatYoullLearn={{
              parseContent: course.productOverview,
            }}
            skillLanguageOverview={{
              associatedSkills: course.associatedSkills,
              prerequisitesHtml: course.prerequisitesHtml,
              syllabusHtml: course.syllabusHtml,
              formattedLanguage: formattedLanguage,
              formattedTranscripts: formattedTranscripts,
              formattedAiLanguages: formattedAiLanguages,
              isCurriculumInMainContent,
            }}
            instructorsList={{
              instructors: course.activeCourseRun?.instructors || [],
            }}
            awards={{
              awards: course.customizations.catalogPage?.awards || [],
              isSpanish: false,
            }}
            benefitsList={{
              benefits: courseBenefitList?.benefitListItems,
              title: courseBenefitList?.listTitle,
            }}
          />
        );
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const category = getIndustryKey({
          title: course.productName,
          subjectEn: course.subjectEn || "",
          subjectEs: course.subjectEs || "",
          overview: course.productOverview,
          subjects: course.subjects,
          skills: course.skills,
        });
        const careerCardsData =
          CAREER_CARDS_VARIANTS[category || "dataScience"];
        return (
          <CareerCards
            location={careerCards?.location || "detail-page"}
            isSideRail={careerCards?.isSideRail || true}
            industry={careerCards?.industry || category || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              course.skills,
              category || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "curriculum",
      renderer: () => {
        const hasPrerequisites = (course.prerequisitesHtml?.length ?? 0) > 0;
        if (!syllabusHtml && !hasPrerequisites) {
          return null;
        }
        return (
          <Curriculum
            key="curriculum"
            syllabus={syllabusHtml ?? ""}
            weeksToComplete={weeksToComplete}
            minEffort={minEffort}
            maxEffort={maxEffort}
            formattedLanguage={formattedLanguage}
            formattedTranscripts={formattedTranscripts}
            formattedAiLanguages={formattedAiLanguages}
            prerequisitesHtml={course.prerequisitesHtml}
          />
        );
      },
    },
    {
      key: "midPageCta",
      renderer: () => {
        return (
          course.activeCourseRun && (
            <div className="[&_h2]:text-base md:[&_h2]:text-3xl py-4 md:py-0">
              <MidPageCTA
                key="midPageCta"
                course={course}
                isArchived={isArchived}
              />
            </div>
          )
        );
      },
    },
    {
      key: "partnershipDescription",
      renderer: () => {
        return course.partners?.length && partnershipDescription ? (
          <PartnershipDescription
            key="partnershipDescription"
            title={partnershipDescription.title}
            prePartnerText={partnershipDescription.prePartnerText}
            postPartnerText={partnershipDescription.postPartnerText}
            partnerLogoList={course.partners?.map(
              (partner: PartialPartner) => partner.logoUrl,
            )}
            partnerNameList={course.partners?.map(
              (partner: PartialPartner) => partner.name,
            )}
            titleClassName="text-2xl md:text-3xl"
          />
        ) : null;
      },
    },
    {
      key: "alumniTestimonials",
      renderer: () => {
        return (
          <AlumniTestimonials
            key="testimonials"
            monarchTestimonials={alumniTestimonials}
            courseTestimonials={course.learnerTestimonials || ""}
          />
        );
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={courseDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
          />
        ) : null;
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return <FAQs key="faqs" course={course} variant="pathway" />;
      },
    },
    {
      key: "fullProgramCta",
      renderer: () =>
        designToDisplay && ["1", "2", "3"].includes(designToDisplay) ? (
          <RelatedProgram
            designTobeSelected={designToDisplay}
            enrichedPrograms={enrichedPrograms || undefined}
            course={course}
            carouselData={carouselData}
            cardType={cardType}
            expandableSection={expandableSection}
          />
        ) : null,
    },
    {
      key: "moreAboutCourse",
      renderer: () => {
        return course.additionalInformation ? (
          <MoreAboutCourse
            key="moreAboutCourse"
            additionalInformation={course.additionalInformation}
          />
        ) : null;
      },
    },
    {
      key: "socialShare",
      renderer: () => {
        return (
          <SocialShare
            key="socialShare"
            productUrl={course.productUrl}
            productName={course.productName}
          />
        );
      },
    },
    {
      key: "enterpriseCta",
      renderer: () => {
        return (
          <div
            className="bg-putty-300"
            key="enterpriseCta"
            style={{
              maxWidth: "none",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            {!course.frequentlyAskedQuestions && course.hasOfacRestrictions && (
              <OFACMessage type="course" />
            )}
            <EnterpriseCTA productName={course.productName} variant="compact" />
          </div>
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreRelatedPrograms"),
              url: getProgramSearchUrl(course.partners),
              html_id: "bundling-cta",
              location: "single-course-detail-page",
              name: "single-course-bundling-banner",
            }}
            partnerName={course.partners?.[0]?.name}
          />
        ) : null;
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        const uuids = [
          course.productUuid,
          ...(productCompare?.uuids || compareFallbackUuids),
        ];

        return productCompare ? (
          <ProductCompare
            productCompare={productCompare}
            productUuids={uuids}
            currentCourseCheckoutUrl={compareCtaUrl}
            location="course-detail-page"
            currentCtaLabel={
              !isArchived && course.activeCourseRun?.isEnrollable
                ? (leadWithPaidData.value?.enrollCtaLabel ??
                  tCourse("advanceYourCareer"))
                : undefined
            }
          />
        ) : null;
      },
    },
  ];

  return (
    <>
      {course.activeCourseRun && (
        <ProductViewTracker
          productId={course.activeCourseRun.key}
          productType="course"
          productName={course.productName}
          productUrl={course.productUrl}
          checkoutUrl={checkoutUrl}
          courseId={course.productUuid}
          metadata={{
            price: coursePrice || undefined,
            currency: "USD",
            partner: course.partners?.[0]?.key,
            category: course.subjectEn ?? course.subjectEs,
            enrollmentStart: course.activeCourseRun.enrollmentStart?.toString(),
            enrollmentEnd: course.activeCourseRun.enrollmentEnd?.toString(),
            courseRunEnd: course.activeCourseRun.end?.toString(),
          }}
        />
      )}
      <CourseTaggedHeader
        course={course}
        displayPrice={coursePrice}
        currency={pricingData?.currency}
        pricingData={pricingData}
        variant={headerBackground === "light" ? "light" : "dark"}
        priceLabel={headerPriceLabel}
        pricePosition={headerPricePosition}
        simpleMobileHeader={simpleMobileHeader}
        paymentOption={paymentOption}
        showDynamicBadges={showDynamicBadges}
      />

      <CourseCurrencyBarSurface
        course={course}
        pricingData={pricingData}
        courseIsEnrollable={course.activeCourseRun?.isEnrollable ?? false}
        isArchived={isArchived}
      />

      {course.activeCourseRun && (
        <StickyCTA course={course} courseRun={course.activeCourseRun} />
      )}

      <div
        className="bg-putty-100 flex flex-col gap-12 sm:gap-16 pt-12"
        id="course-decided-details-body-container"
      >
        <div>
          <div className={mainContentClass}>
            {getSortedComponentsBySectionOrder({
              componentList,
              sectionsOrder: mainContent?.map(
                (component) => component.component,
              ),
            })}
          </div>
        </div>
      </div>
    </>
  );
}
